module Meevo.Plugins.Register {

    export class RegisterDiscountService {
        private securityKeys = Meevo.Components.Common.SecurityKeys;
        private discountLabels: any[];
        private meevoCurrency: any;

        constructor(
            public $q: ng.IQService,
            public $translate: angular.translate.ITranslateService,
            public $filter: angular.IFilterService,
            public dataService: Meevo.Components.BaseClasses.IDataService,
            public modalService: Meevo.Components.Modal.IModalService,
            public securityService: Meevo.Components.Common.ISecurityService,
            public identityService: Meevo.Components.Common.IIdentityService,
            public printService: Meevo.Components.Common.IPrintService,
            public roundingService: Meevo.Components.Common.IRoundingService
        ) {
            this.meevoCurrency = this.$filter("meevoCurrency") as any;
        }

        getDiscountLabels() {
            this.dataService.getEnumList(Meevo.Model.EnumTypeEnum.DiscountTypeEnum).then((data) => {
                this.discountLabels = data;
            });
        }

        isDiscountable(saleLine: Model.SaleLine): boolean {
            if (saleLine.isRefund) {
                return false;
            }

            switch (saleLine.saleType) {
                case Model.SalesTypeEnum.PackageRefund:
                case Model.SalesTypeEnum.ClientAccountCredit:
                case Model.SalesTypeEnum.ClientAccountPayment:
                case Model.SalesTypeEnum.DepositCredit:
                case Model.SalesTypeEnum.BulkGiftCard:
                case Model.SalesTypeEnum.PaymentPlanPayment:
                    return false;
            }

            if (saleLine.saleType === Model.SalesTypeEnum.GiftCard && (<GiftCardSaleLineItem>saleLine.item).clientGiftCard.giftCardType.isPromo) {
                return false;
            }

            if (saleLine.saleType === Model.SalesTypeEnum.Membership && !(<MembershipSaleLineItem>saleLine.item).isBillable) {
                return false;
            }

            return true;
        }

        isSaleDiscountable(saleLines: Model.SaleLine[]) {
            return _(saleLines).find((saleLine) => this.isDiscountable(saleLine)) != null;
        }

        canApplyDiscount(saleLine: Model.SaleLine): boolean {
            var canApply = true;
            if (saleLine.discountLines && _(saleLine.discountLines).findWhere({ saleLineDiscountType: Model.SaleLineDiscountTypeEnum.ItemOnSale, isStackable: false })) {
                canApply = false;
            }
            return canApply;
        }

        getSecurityKeyForApplyDiscount(saleLine) {
            if (saleLine.saleType === Model.SalesTypeEnum.GiftCard ||
                saleLine.saleType === Model.SalesTypeEnum.ReloadGiftCard) {
                return Meevo.Components.Common.SecurityKeys.REGISTER_REGISTER_GIFTCARDS_APPLYDISCOUNT;
            } else if (saleLine.saleType === Model.SalesTypeEnum.Membership ||
                saleLine.saleType === Model.SalesTypeEnum.MembershipRenewal) {
                return Meevo.Components.Common.SecurityKeys.REGISTER_REGISTER_MEMBERSHIP_APPLYDISCOUNT;
            } else {
                return Meevo.Components.Common.SecurityKeys.REGISTER_REGISTER_ACTION_APPLYDISCOUNT;
            }
        }

        private isSystemSpecialPriceDiscount(discount): boolean {
            return discount.type === Model.DiscountTypeEnum.SpecialPrice &&
                (discount.discountId == null || Guid.empty.equals(new Guid(discount.discountId)));
        }

        isPriceChangeDiscountSame(saleLine: Model.SaleLine, newFinalItemPrice: number, newFinalItemCharge: number): boolean {
            return Math.abs(saleLine.finalItemPrice) === newFinalItemPrice &&
                (newFinalItemCharge === null || Math.abs(saleLine.finalItemCharge) === newFinalItemCharge);
        }

        handlePriceChangeDiscount(context, saleLine: Model.SaleLine, newFinalItemPrice: number, newFinalItemCharge: number, isGiftCardTypeDiscount: boolean = false) {
            if (this.isPriceChangeDiscountSame(saleLine, newFinalItemPrice, newFinalItemCharge)) {
                return;
            }

            // Initialize discount lines
            if (!saleLine.discountLines) {
                saleLine.discountLines = [];
            }

            const priceDiscount = context.subtractNormalizedNumbers(Math.abs(saleLine.baseItemPrice), Math.abs(newFinalItemPrice));
            const chargeDiscount = context.subtractNormalizedNumbers(Math.abs(saleLine.baseItemCharge), Math.abs(newFinalItemCharge || saleLine.finalItemCharge));

            // Update last discount if it is a special price
            let lastDiscount: any = _.last(saleLine.discountLines);

            if (lastDiscount && this.isSystemSpecialPriceDiscount(lastDiscount)) {
                if (saleLine.discountLines.length === 1 && priceDiscount === 0 && chargeDiscount === 0) {
                    saleLine.discountLines = [];
                    return;
                }

                lastDiscount.discountValue = newFinalItemPrice;
            }
            else {
                if (saleLine.discountLines.length === 0 && priceDiscount === 0 && chargeDiscount === 0) {
                    return;
                }

                // Create specific discount and add to saleline discount lines
                lastDiscount = {
                    discountId: null,
                    discountValue: newFinalItemPrice,
                    displayName: null,
                    isBuyGet: false,
                    isStackable: true,
                    saleLineDiscountType: Model.SaleLineDiscountTypeEnum.Manual,
                    tag: null,
                    type: Model.DiscountTypeEnum.SpecialPrice,
                    isAutoApply: false,
                    isGiftCardTypeDiscount: isGiftCardTypeDiscount,
                };

                saleLine.discountLines.push(lastDiscount);
            }

            // discount logic will not run for refund lines, so need to populate the amount field
            // but it only works if it is the only SpecialPrice discount

            if (saleLine.isRefund &&
                saleLine.discountLines.length === 1 &&
                this.isSystemSpecialPriceDiscount(lastDiscount)) {
                if (saleLine.discountLines.length === 1 && priceDiscount === 0 && chargeDiscount === 0) {
                    saleLine.discountLines = [];
                    return;
                }

                lastDiscount.amount = priceDiscount;
            }
        }

        handleDiscountValidation(context, response, keepAccordionOpen, selectSection, checkWarnings) {
            let recalc = false;

            let isPending = _(context.sale.saleLines).any((saleLine) => {
                return _(saleLine.discountLines).any((discountLine) => {
                    return discountLine.isPending;
                });
            });

            if (isPending) {
                this.securityService.checkSecurityItem(this.securityKeys.REGISTER_REGISTER_MEMBERSHIP_APPLYDISCOUNT_OVERRIDEMINMAX).then((item: Model.SecurityItem) => {
                    if (item.securityAccess == Model.SecurityAccessEnum.Grant) {
                        this.processDiscounts(context, context.sale, true);
                        context.sale.automaticDiscountIdToBeApplied = response.automaticDiscountIdToBeApplied;
                        context.recalculateSale(keepAccordionOpen, selectSection, checkWarnings);
                    }
                    else if (item.securityAccess == Model.SecurityAccessEnum.Deny && item.canElevate) {
                        let defer = this.$q.defer();

                        defer.promise.then(() => {
                            this.processDiscounts(context, context.sale, true);
                            recalc = true;
                        }).catch(() => {
                            this.processDiscounts(context, context.sale, false);
                        }).finally(() => {
                            if (recalc) {
                                context.sale.automaticDiscountIdToBeApplied = response.automaticDiscountIdToBeApplied;
                                context.recalculateSale(keepAccordionOpen, selectSection, checkWarnings);
                            }
                        });

                        context.$rootScope.$broadcast(
                            Meevo.Components.Common.Messages.ELEVATION_CHALLENGE,
                            defer,
                            item.key.replace(/:/gi, '_'),
                            item.displayName,
                            this.identityService.current().userName,
                            item.securityKeyPath
                        );
                    } else {
                        this.processDiscounts(context, context.sale, false);
                        this.securityService.showDenyWarning(item.securityKeyPath);
                    }
                });
            }
        }

        private processDiscounts(context, sale, keepDiscount) {
            sale.saleLines.forEach(saleLine => {
                for (let i = saleLine.discountLines.length - 1; i >= 0; i--) {
                    let discountLine = saleLine.discountLines[i];

                    if (discountLine.isPending) {
                        if (keepDiscount) {
                            discountLine.isPending = false;
                        } else {
                            saleLine.discountLines.splice(i, 1);
                            if (discountLine.isAutoApply) {
                                sale.removedAutomaticDiscountIds = sale.removedAutomaticDiscountIds || [];

                                if (sale.removedAutomaticDiscountIds.indexOf(discountLine.discountId) == -1) {
                                    sale.removedAutomaticDiscountIds.push(discountLine.discountId);
                                }
                            }
                        }
                    }
                }

                if (!keepDiscount && context.selectedSection.sectionType == RegisterSectionTypeEnum.AutomaticDiscounts) {
                    context.selectedSection.reset();
                }
            });
        }

        addDiscount(context, saleLine: Model.SaleLine) {
            // need to check if the sale line has discount that cannot be combined with other discounts
            // except Yield Management stackable which still allows manual discounts

            const isNotStackable = _.chain(saleLine.discountLines)
                .filter(dl => dl.saleLineDiscountType !== Meevo.Model.SaleLineDiscountTypeEnum.YieldManagement)
                .any(dl => !dl.isStackable)
                .value();

            if (isNotStackable) {
                this.modalService.alert("MESSAGE_HAS_DISCOUNT_THAT_CANNOT_BE_COMBINED");
            }
            else {
                context.hideCheckoutButtons = true;

                context.selectSection(RegisterSectionTypeEnum.Discounts).then(() => {
                    (context.selectedSection as DiscountsSection).initialize(saleLine);
                });
            }
        }

        editDiscount(context, saleLine: Model.SaleLine, discountItem: Model.DiscountLine) {
            if (this.isDiscountMembershipBenefit(discountItem) ||
                this.isDiscountItemOnSale(discountItem) ||
                this.isDiscountYieldManagement(discountItem) ||
                this.isDiscountFee(discountItem)) {
                return;
            }

            context.selectSection(RegisterSectionTypeEnum.Discounts).then(() => {
                (context.selectedSection as DiscountsSection).initialize(saleLine, discountItem);
            });
        }

        isDiscountRemovable(discountItem: Model.DiscountLine): boolean {
            const isNotRemovable =
                this.isDiscountItemOnSale(discountItem) ||
                this.isDiscountYieldManagement(discountItem) ||
                this.isDiscountFee(discountItem);

            return !isNotRemovable;
        }

        private isDiscountItemOnSale(discountItem: Model.DiscountLine): boolean {
            return discountItem && discountItem.saleLineDiscountType === Meevo.Model.SaleLineDiscountTypeEnum.ItemOnSale;
        }

        private isDiscountYieldManagement(discountItem: Model.DiscountLine): boolean {
            return discountItem && discountItem.saleLineDiscountType === Meevo.Model.SaleLineDiscountTypeEnum.YieldManagement;
        }

        private isDiscountCancellationFee(discountItem: Model.DiscountLine): boolean {
            return discountItem && discountItem.saleLineDiscountType === Meevo.Model.SaleLineDiscountTypeEnum.CancellationFee;
        }

        private isDiscountNoShowFee(discountItem: Model.DiscountLine): boolean {
            return discountItem && discountItem.saleLineDiscountType === Meevo.Model.SaleLineDiscountTypeEnum.NoShowFee;
        }

        private isDiscountMembershipBenefit(discountItem: Model.DiscountLine): boolean {
            return discountItem && discountItem.saleLineDiscountType === Meevo.Model.SaleLineDiscountTypeEnum.MembershipBenefit;
        }

        private isDiscountFee(discountItem: Model.DiscountLine): boolean {
            return this.isDiscountCancellationFee(discountItem) || this.isDiscountNoShowFee(discountItem);
        }

        getDiscountName(discountItem: Model.DiscountLine): string {
            if (!discountItem) {
                return "";
            }

            if (discountItem.displayName) {
                let prefix = "";

                if (this.isDiscountYieldManagement(discountItem)) {
                    prefix = this.$translate.instant("LABEL_YIELD_MANAGEMENT_DISCOUNT_PREFIX") + ': ';
                }

                return `${prefix}${discountItem.displayName}`;
            }

            let displayName: string = "";

            if (this.discountLabels && this.discountLabels.length > 0) {
                const discountEnum = _(this.discountLabels).findWhere({ id: discountItem.type });
                if (discountEnum) {
                    displayName = discountEnum.displayName;
                }
            }

            if (displayName == "") {
                if (Meevo.Model.DiscountTypeEnum[discountItem.type] != null) {
                    const discountLabelForTranslation = `LABEL_DISCOUNT_NAME_${discountItem.type}`;
                    displayName = this.$translate.instant(discountLabelForTranslation);
                    if (displayName == discountLabelForTranslation) {
                        displayName = Meevo.Model.DiscountTypeEnum[discountItem.type];
                    }
                }
                else {
                    displayName = this.$translate.instant("LABEL_DISCOUNT");
                }
            }

            switch (discountItem.saleLineDiscountType) {
                case Model.SaleLineDiscountTypeEnum.MembershipBenefit:
                    displayName = this.$translate.instant('LABEL_MEMBER_DISCOUNT_APPLIED') + `: ${displayName}`;
                    break;
                case Model.SaleLineDiscountTypeEnum.CancellationFee:
                    displayName = this.$translate.instant('LABEL_DISCOUNT_CANCELLATION_FEE');
                    break;
                case Model.SaleLineDiscountTypeEnum.NoShowFee:
                    displayName = this.$translate.instant('LABEL_DISCOUNT_NO_SHOW_FEE');
                    break;
            }

            return displayName;
        }

        getDiscountValue(discountItem: Model.DiscountLine) {
            if (!discountItem) {
                return "";
            }

            let displayString = "";

            switch (discountItem.type) {
                case Model.DiscountTypeEnum.AmountOff:
                    displayString = this.meevoCurrency(-discountItem.discountValue, 'financial');
                    break;

                case Model.DiscountTypeEnum.PercentOff:
                    let prefix = discountItem.discountValue < 0
                        ? this.$translate.instant("LABEL_DISCOUNT_INCREASE")
                        : "";

                    const discountValue = this.roundingService.normalizeNumber(Math.abs(discountItem.discountValue) * 100, 0);
                    displayString = `${prefix}${discountValue}%`;
                    break;

                case Model.DiscountTypeEnum.SpecialPrice:
                    displayString = this.meevoCurrency(discountItem.discountValue, 'financial');
                    break;
            }

            return displayString;
        }

        getLastSpecialPriceIndex(discountLines: Meevo.Model.DiscountLine[]): number {
            if (!discountLines || discountLines.length === 0) {
                return -1;
            }

            for (let index = discountLines.length - 1; index >= 0; index--) {
                let discount = discountLines[index];
                if (discount.type === Meevo.Model.DiscountTypeEnum.SpecialPrice) {
                    return index;
                }
            }

            return -1;
        }

        removeDiscountItem(context, saleLine: Model.SaleLine, discountLineIndex: number) {
            let discountLine = saleLine.discountLines[discountLineIndex];

            const continueRemoveDiscount = () => {
                if (discountLine.saleLineDiscountType === Meevo.Model.SaleLineDiscountTypeEnum.MembershipBenefit) {
                    if (!saleLine.removedAutomaticDiscountIds) {
                        saleLine.removedAutomaticDiscountIds = [];
                    }
                    saleLine.removedAutomaticDiscountIds.push(discountLine.membershipBenefitTemplateId);
                    saleLine.discountLines.splice(discountLineIndex, 1);
                }
                else if (!!discountLine.discountId && (discountLine.isAutoApply || discountLine.isOffer)) {
                    if (!saleLine.removedAutomaticDiscountIds) {
                        saleLine.removedAutomaticDiscountIds = [];
                    }
                    saleLine.removedAutomaticDiscountIds.push(discountLine.discountId);
                    saleLine.discountLines.splice(discountLineIndex, 1);
                }
                else if (!!discountLine.discountId && !!discountLine.discountSaleLineGroupId && discountLine.isBuyGet) {
                    context.sale.saleLines.forEach((saleLine: Model.SaleLine) => {
                        var discountNdx = _.findIndex(saleLine.discountLines, (dl) => { return (dl.discountSaleLineGroupId == discountLine.discountSaleLineGroupId); });
                        if (discountNdx >= 0) {
                            saleLine.discountLines.splice(discountNdx, 1);
                        }
                    });
                }
                else {
                    saleLine.discountLines.splice(discountLineIndex, 1);
                }

                this.printService.queueMessagesToPoleDisplay({
                    lines: [
                        { text: ["*VOID*" + this.getDiscountName(discountLine)], alignment: Meevo.Components.Common.PoleDisplayAlignment.Left },
                        { text: [this.meevoCurrency(-discountLine.amount, 'financial')], alignment: Meevo.Components.Common.PoleDisplayAlignment.Right }
                    ]
                });

                this.reEvaluateIsFullCommissionOnSaleLine(saleLine);

                // remove redemptions for this package when updating discounts
                if (context.isPackageType(saleLine)) {
                    context.removeRedemptionForPackage(saleLine.item.id);
                }

                var promise = context.recalculateSale(true);

                if (context.selectedSection && context.selectedSection.sectionType === RegisterSectionTypeEnum.Discounts) {
                    context.selectSection(RegisterSectionTypeEnum.Services);
                }
                else if (context.selectedSection && context.selectedSection.sectionType === RegisterSectionTypeEnum.AutomaticDiscounts && discountLine.isAutoApply) {
                    promise.then(() => {
                        context.selectedSection.reset();
                    });
                }
            };

            // if discount is the last special price discount that matches the gift card type
            // then prompt if the user wants to remove the discount
            const giftCardSaleLineItem = saleLine.item as Meevo.Model.GiftCardSaleLineItem;
            if (saleLine.saleType === Meevo.Model.SalesTypeEnum.GiftCard &&
                saleLine.finalItemPrice !== saleLine.baseItemPrice &&
                giftCardSaleLineItem &&
                giftCardSaleLineItem.clientGiftCard.giftCardType &&
                saleLine.baseItemPrice === giftCardSaleLineItem.clientGiftCard.giftCardType.valuePrice &&
                discountLine.discountValue === giftCardSaleLineItem.clientGiftCard.giftCardType.defaultPrice &&
                this.isSystemSpecialPriceDiscount(discountLine) &&
                this.getLastSpecialPriceIndex(saleLine.discountLines) === discountLineIndex
            ) {
                this.modalService.confirm("MESSAGE_IS_GIFT_CARD_TYPE_DISCOUNT").then((response) => {
                    if (response) {
                        continueRemoveDiscount();
                    }
                });
            } else {
                continueRemoveDiscount();
            }
        }

        private reEvaluateIsFullCommissionOnSaleLine(saleLine: Model.SaleLine) {
            if (saleLine.discountLines.length == 0) {
                saleLine.isFullCommissionApplicableManuallyChecked = null;
                saleLine.isFullCommissionApplicable = false;
            }
            else if (saleLine.isFullCommissionApplicableManuallyChecked == null) {
                // re-evaluate isFullCommissionApplicable
                saleLine.isFullCommissionApplicable = (_.findWhere(saleLine.discountLines, { isFullCommissionApplicable: true }) != null);
            }
        }

        isPriceDiscounted(context, saleLine: Model.SaleLine) {
            return ((saleLine.discountLines && saleLine.discountLines.length > 0)
                || (saleLine.redeemedPackages && saleLine.redeemedPackages.length > 0 && saleLine.packageRedemptionType !== 0)
                || (context.isSaleLineAService(saleLine) && (saleLine.item as Model.ServiceSaleLineItem).paymentPromoGiftCardId))
                && saleLine.basePriceCharge != saleLine.finalPriceCharge;
        }

        hasDiscountAffectingCommissions(saleLine: Model.SaleLine): boolean {
            return (saleLine.discountLines
                && _(saleLine.discountLines).find((discount) => {
                    return (discount.saleLineDiscountType !== Model.SaleLineDiscountTypeEnum.ItemOnSale
                        && discount.saleLineDiscountType !== Model.SaleLineDiscountTypeEnum.CancellationFee
                        && discount.saleLineDiscountType !== Model.SaleLineDiscountTypeEnum.MembershipBenefit
                        && !discount.isBuyItem);
                }) != null);
        }

        resetDiscounts(context) {
            if (context.selectedSection.sectionType === RegisterSectionTypeEnum.Discounts) {
                let discountSection = (context.selectedSection as DiscountsSection);
                discountSection.reset();
            }
        }

        reinitializeDiscounts(context, saleLine) {
            if (context.selectedSection.sectionType === RegisterSectionTypeEnum.Discounts) {
                let discountSection = (context.selectedSection as DiscountsSection);
                discountSection.reInitialize(saleLine);
            }
        }

        static factory(): Function {
            let svc = (
                $q,
                $translate,
                $filter,
                dataService,
                modalService,
                securityService,
                identityService,
                printService,
                roundingService
            ) => new RegisterDiscountService(
                $q,
                $translate,
                $filter,
                dataService,
                modalService,
                securityService,
                identityService,
                printService,
                roundingService
            );

            svc.$inject = [
                "$q",
                "$translate",
                "$filter",
                "meevo.components.common.DataService",
                "meevo.components.modal.ModalService",
                "meevo.components.common.SecurityService",
                "meevo.components.common.IdentityService",
                "meevo.components.common.PrintService",
                "meevo.components.common.RoundingService",
            ];

            return svc;
        }
    }

    angular.module("meevo.plugins.register")
        .factory("meevo.plugins.register.RegisterDiscountService", RegisterDiscountService.factory());
}
